import React, { useEffect, useMemo, useState } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  ScrollView,
  Modal,
  Image,
  ActivityIndicator,
  Platform,
  StyleSheet,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import * as DocumentPicker from 'expo-document-picker';
import { useVenues } from '../VenueContext';
import {
  MAX_EVENT_WEBSITES,
  createEventPost,
  deleteReplacedEventMedia,
  eventDisplayName,
  isVideoMime,
  updateEventPost,
  uploadEventFile,
} from '../../utils/eventPostsService';

const GREEN = '#03BB8D';
const PAGE_BG = '#231F20';
const LIGHT = '#E2DEDE';

const emptyLink = () => ({ text: '', url: '' });
const storedAssetData = (asset) => Object.keys(asset || {}).reduce((stored, key) => {
  if (!['previewUri', 'existing', 'selected', 'file'].includes(key)) stored[key] = asset[key];
  return stored;
}, {});

async function pickFile(types) {
  const result = await DocumentPicker.getDocumentAsync({ type: types, copyToCacheDirectory: true, multiple: false });
  if (result.canceled || !result.assets?.length) return null;
  const asset = result.assets[0];
  // On web the picked File is on asset.file; uploadEventFile accepts either.
  return { ...asset, previewUri: asset.uri };
}

function Label({ children, hint }) {
  return (
    <View style={{ marginBottom: 6 }}>
      <Text style={styles.label}>{children}</Text>
      {hint ? <Text style={styles.hint}>{hint}</Text> : null}
    </View>
  );
}

/** Link editor: visible text + URL. */
function LinkFields({ value, onChange, textPlaceholder, onRemove }) {
  return (
    <View style={styles.linkRow}>
      <TextInput
        style={[styles.input, { flex: 1, minWidth: 0 }]}
        value={value.text}
        onChangeText={(text) => onChange({ ...value, text })}
        placeholder={textPlaceholder}
        placeholderTextColor="rgba(226,222,222,0.45)"
      />
      <TextInput
        style={[styles.input, { flex: 1.4, minWidth: 0 }]}
        value={value.url}
        onChangeText={(url) => onChange({ ...value, url })}
        placeholder="https://..."
        placeholderTextColor="rgba(226,222,222,0.45)"
        autoCapitalize="none"
        keyboardType={Platform.OS === 'web' ? 'default' : 'url'}
      />
      {onRemove ? (
        <TouchableOpacity onPress={onRemove} style={styles.iconButton} accessibilityLabel="Remove website">
          <Ionicons name="trash-outline" size={18} color="rgba(231,76,60,0.85)" />
        </TouchableOpacity>
      ) : null}
    </View>
  );
}

/**
 * Create an event post: poster, title, date, websites (up to 10), ticket link,
 * optional venue, organizer (photo, name, description) and event details.
 */
export function EventPostFormModal({ visible, onClose, currentUser, onPosted, event = null }) {
  const { venues = [] } = useVenues() || {};
  const [poster, setPoster] = useState(null);
  const [title, setTitle] = useState('');
  const [date, setDate] = useState('');
  const [websites, setWebsites] = useState([emptyLink()]);
  const [ticketLink, setTicketLink] = useState(emptyLink());
  const [venueQuery, setVenueQuery] = useState('');
  const [venue, setVenue] = useState(null);
  const [organizerPhoto, setOrganizerPhoto] = useState(null);
  const [organizerName, setOrganizerName] = useState('');
  const [organizerDescription, setOrganizerDescription] = useState('');
  const [details, setDetails] = useState('');
  const [talkEnabled, setTalkEnabled] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!visible) return;
    if (!event) {
      setPoster(null);
      setTitle('');
      setDate('');
      setWebsites([emptyLink()]);
      setTicketLink(emptyLink());
      setVenueQuery('');
      setVenue(null);
      setOrganizerPhoto(null);
      setOrganizerName('');
      setOrganizerDescription('');
      setDetails('');
      setTalkEnabled(false);
      setError('');
      return;
    }

    setPoster(event.poster ? { ...event.poster, previewUri: event.poster.url, existing: true } : null);
    setTitle(event.title || '');
    setDate(event.date || '');
    setWebsites(Array.isArray(event.websites) && event.websites.length
      ? event.websites.map((site) => ({ text: site.text || '', url: site.url || '' }))
      : [emptyLink()]);
    setTicketLink({ text: event.ticketLink?.text || '', url: event.ticketLink?.url || '' });
    setVenue(event.venueId ? { id: event.venueId, name: event.venueName || '' } : null);
    setVenueQuery(event.venueId ? event.venueName || '' : '');
    setOrganizerPhoto(event.organizer?.photo
      ? { ...event.organizer.photo, previewUri: event.organizer.photo.url, existing: true }
      : null);
    setOrganizerName(event.organizer?.name || '');
    setOrganizerDescription(event.organizer?.description || '');
    setDetails(event.details || '');
    setTalkEnabled(event.talkEnabled !== false);
    setError('');
  }, [visible, event?.id]);

  const venueMatches = useMemo(() => {
    const q = venueQuery.trim().toLowerCase();
    if (!q || venue) return [];
    return venues
      .filter((v) => `${v.name || ''} ${v.city || ''}`.toLowerCase().includes(q))
      .slice(0, 6);
  }, [venueQuery, venues, venue]);

  const reset = () => {
    setPoster(null); setTitle(''); setDate(''); setWebsites([emptyLink()]);
    setTicketLink(emptyLink()); setVenueQuery(''); setVenue(null);
    setOrganizerPhoto(null); setOrganizerName(''); setOrganizerDescription('');
    setDetails(''); setError('');
    setTalkEnabled(false);
  };

  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose?.();
  };

  const handlePickPoster = async () => {
    const file = await pickFile(['image/*', 'video/*']);
    if (file) setPoster({ ...file, selected: true });
  };

  const handlePickOrganizerPhoto = async () => {
    const file = await pickFile(['image/*']);
    if (file) setOrganizerPhoto({ ...file, selected: true });
  };

  const handleSubmit = async () => {
    if (!currentUser?.uid) return;
    if (!poster) { setError('Upload the main poster.'); return; }
    if (!title.trim()) { setError('Add a title.'); return; }
    if (!date.trim()) { setError('Add the event date.'); return; }
    setError('');
    setSubmitting(true);
    try {
      const uid = currentUser.uid;
      const uploadedPoster = poster.selected
        ? await uploadEventFile(poster.file || poster, uid, 'posters')
        : poster;
      const uploadedOrganizerPhoto = organizerPhoto?.selected
        ? await uploadEventFile(organizerPhoto.file || organizerPhoto, uid, 'organizers')
        : organizerPhoto;
      const posterIsVideo = isVideoMime(uploadedPoster.mimeType || poster.mimeType, uploadedPoster.name);
      const payload = {
        title: title.trim(),
        date: date.trim(),
        websites: websites
          .filter((w) => w.url.trim())
          .slice(0, MAX_EVENT_WEBSITES)
          .map((w) => ({ text: w.text.trim(), url: w.url.trim() })),
        ticketLink: ticketLink.url.trim()
          ? { text: ticketLink.text.trim(), url: ticketLink.url.trim() }
          : null,
        venueId: venue?.id || null,
        venueName: venue?.name || null,
        organizer: {
          name: organizerName.trim(),
          description: organizerDescription.trim(),
          photo: uploadedOrganizerPhoto ? storedAssetData(uploadedOrganizerPhoto) : null,
        },
        details: details.trim(),
        talkEnabled,
        poster: {
          ...storedAssetData(uploadedPoster),
          mimeType: uploadedPoster.mimeType || poster.mimeType || '',
        },
        mediaType: posterIsVideo ? 'videos' : 'photos',
      };
      if (event) {
        await updateEventPost(event.id, payload);
        const cleanupErrors = await deleteReplacedEventMedia(
          event,
          payload.poster,
          payload.organizer.photo,
        );
        if (cleanupErrors.length) {
          console.error('Event updated, but some replaced media could not be removed:', cleanupErrors);
        }
      } else {
        await createEventPost({
          ...payload,
          userId: uid,
          username: eventDisplayName(currentUser),
        });
      }
      reset();
      onPosted?.();
    } catch (err) {
      console.error(event ? 'Update event post failed' : 'Create event post failed', err);
      setError(event ? 'Could not update the event. Please try again.' : 'Could not post the event. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const posterIsVideo = poster && isVideoMime(poster.mimeType, poster.name);

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={handleClose}>
      <View style={styles.backdrop}>
        <View style={styles.sheet}>
          <View style={styles.header}>
            <Text style={styles.headerTitle}>{event ? 'Edit Event' : 'Post an Event'}</Text>
            <TouchableOpacity onPress={handleClose} style={styles.iconButton} accessibilityLabel="Close">
              <Ionicons name="close" size={22} color={LIGHT} />
            </TouchableOpacity>
          </View>

          <ScrollView contentContainerStyle={styles.body} keyboardShouldPersistTaps="handled">
            {/* Main poster */}
            <View>
              <Label hint="Image or video, shown at the top of the post.">Main poster *</Label>
              <TouchableOpacity onPress={handlePickPoster} style={styles.posterPicker} activeOpacity={0.8}>
                {poster && !posterIsVideo ? (
                  <Image source={{ uri: poster.previewUri }} style={styles.posterPreview} resizeMode="cover" />
                ) : poster ? (
                  <View style={styles.pickerEmpty}>
                    <Ionicons name="videocam-outline" size={34} color={GREEN} />
                    <Text style={styles.pickerText} numberOfLines={1}>{poster.name}</Text>
                  </View>
                ) : (
                  <View style={styles.pickerEmpty}>
                    <Ionicons name="cloud-upload-outline" size={34} color={GREEN} />
                    <Text style={styles.pickerText}>Upload poster</Text>
                  </View>
                )}
              </TouchableOpacity>
            </View>

            <View>
              <Label>Title *</Label>
              <TextInput style={styles.input} value={title} onChangeText={setTitle} placeholder="Event title" placeholderTextColor="rgba(226,222,222,0.45)" />
            </View>

            <View>
              <Label>Date *</Label>
              {Platform.OS === 'web' ? (
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  style={{ ...StyleSheet.flatten(styles.input), colorScheme: 'dark', fontFamily: 'Inter, sans-serif', fontSize: 15 }}
                />
              ) : (
                <TextInput style={styles.input} value={date} onChangeText={setDate} placeholder="YYYY-MM-DD" placeholderTextColor="rgba(226,222,222,0.45)" />
              )}
            </View>

            {/* Websites */}
            <View>
              <Label hint={`Link text is optional; the URL shows if it's empty. Up to ${MAX_EVENT_WEBSITES}.`}>Websites</Label>
              {websites.map((site, index) => (
                <LinkFields
                  key={index}
                  value={site}
                  textPlaceholder="Link text"
                  onChange={(next) => setWebsites((prev) => prev.map((w, i) => (i === index ? next : w)))}
                  onRemove={websites.length > 1 ? () => setWebsites((prev) => prev.filter((_, i) => i !== index)) : null}
                />
              ))}
              {websites.length < MAX_EVENT_WEBSITES ? (
                <TouchableOpacity onPress={() => setWebsites((prev) => [...prev, emptyLink()])} style={styles.addButton}>
                  <Ionicons name="add" size={14} color={GREEN} />
                  <Text style={styles.addButtonText}>Add website</Text>
                </TouchableOpacity>
              ) : null}
            </View>

            <View>
              <Label hint="The text people click, and where it goes.">Ticket link</Label>
              <LinkFields value={ticketLink} onChange={setTicketLink} textPlaceholder="e.g. Get tickets" />
            </View>

            {/* Venue (optional) */}
            <View>
              <Label hint="Links the post to a venue page.">Venue (optional)</Label>
              {venue ? (
                <View style={styles.selectedVenue}>
                  <Text style={styles.selectedVenueText} numberOfLines={1}>
                    {venue.name}{venue.city ? ` · ${venue.city}` : ''}
                  </Text>
                  <TouchableOpacity onPress={() => { setVenue(null); setVenueQuery(''); }} style={styles.iconButton} accessibilityLabel="Clear venue">
                    <Ionicons name="close-circle-outline" size={18} color={LIGHT} />
                  </TouchableOpacity>
                </View>
              ) : (
                <>
                  <TextInput style={styles.input} value={venueQuery} onChangeText={setVenueQuery} placeholder="Search venues..." placeholderTextColor="rgba(226,222,222,0.45)" />
                  {venueMatches.map((v) => (
                    <TouchableOpacity key={v.id} onPress={() => setVenue(v)} style={styles.venueOption}>
                      <Text style={styles.venueOptionText} numberOfLines={1}>
                        {v.name}{v.city ? ` · ${v.city}` : ''}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </>
              )}
            </View>

            {/* Organizer */}
            <View>
              <Label>Organizer details</Label>
              <View style={styles.organizerRow}>
                <TouchableOpacity onPress={handlePickOrganizerPhoto} style={styles.organizerPhoto} activeOpacity={0.8} accessibilityLabel="Upload organizer photo">
                  {organizerPhoto
                    ? <Image source={{ uri: organizerPhoto.previewUri }} style={{ width: '100%', height: '100%' }} />
                    : <Ionicons name="camera-outline" size={26} color={PAGE_BG} />}
                </TouchableOpacity>
                <View style={{ flex: 1, gap: 8 }}>
                  <TextInput style={styles.input} value={organizerName} onChangeText={setOrganizerName} placeholder="Organizer name" placeholderTextColor="rgba(226,222,222,0.45)" />
                  <TextInput
                    style={[styles.input, { minHeight: 64, textAlignVertical: 'top' }]}
                    value={organizerDescription}
                    onChangeText={setOrganizerDescription}
                    placeholder="Organizer description"
                    placeholderTextColor="rgba(226,222,222,0.45)"
                    multiline
                  />
                </View>
              </View>
            </View>

            <View>
              <Label>Event details</Label>
              <TextInput
                style={[styles.input, { minHeight: 110, textAlignVertical: 'top' }]}
                value={details}
                onChangeText={setDetails}
                placeholder="Lineup, times, age limit, anything people should know..."
                placeholderTextColor="rgba(226,222,222,0.45)"
                multiline
              />
            </View>

            <TouchableOpacity
              style={styles.talkToggleRow}
              onPress={() => setTalkEnabled((enabled) => !enabled)}
              accessibilityRole="switch"
              accessibilityState={{ checked: talkEnabled }}
            >
              <View style={[styles.talkToggle, talkEnabled && styles.talkToggleEnabled]}>
                <View style={[styles.talkToggleKnob, talkEnabled && styles.talkToggleKnobEnabled]} />
              </View>
              <View style={styles.talkToggleCopy}>
                <Text style={styles.talkToggleTitle}>Enable talk</Text>
                <Text style={styles.hint}>Allow attendees to chat anonymously on this event.</Text>
              </View>
            </TouchableOpacity>

            {error ? <Text style={styles.error}>{error}</Text> : null}

            <View style={styles.actions}>
              <TouchableOpacity onPress={handleClose} disabled={submitting} style={styles.cancelButton}>
                <Text style={styles.cancelText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity onPress={handleSubmit} disabled={submitting} style={[styles.submitButton, submitting && { opacity: 0.6 }]}>
                {submitting ? <ActivityIndicator color={PAGE_BG} /> : <Text style={styles.submitText}>{event ? 'Save Changes' : 'Post'}</Text>}
              </TouchableOpacity>
            </View>
          </ScrollView>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.7)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
  },
  sheet: {
    width: '100%',
    maxWidth: 560,
    maxHeight: '92%',
    backgroundColor: PAGE_BG,
    borderRadius: 24,
    borderWidth: 4,
    borderColor: GREEN,
    overflow: 'hidden',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingTop: 16,
    paddingBottom: 8,
  },
  headerTitle: {
    color: LIGHT,
    fontSize: 24,
    fontFamily: 'Computer Says No',
  },
  body: {
    paddingHorizontal: 20,
    paddingBottom: 24,
    gap: 18,
  },
  label: {
    color: GREEN,
    fontSize: 13,
    fontFamily: 'Inter',
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.6,
  },
  hint: {
    color: 'rgba(226,222,222,0.6)',
    fontSize: 12,
    fontFamily: 'Inter',
    marginTop: 2,
  },
  talkToggleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 8,
  },
  talkToggle: {
    width: 42,
    height: 24,
    borderRadius: 12,
    padding: 3,
    justifyContent: 'center',
    backgroundColor: 'rgba(226,222,222,0.25)',
  },
  talkToggleEnabled: {
    backgroundColor: GREEN,
  },
  talkToggleKnob: {
    width: 18,
    height: 18,
    borderRadius: 9,
    backgroundColor: LIGHT,
  },
  talkToggleKnobEnabled: {
    alignSelf: 'flex-end',
    backgroundColor: PAGE_BG,
  },
  talkToggleCopy: {
    flex: 1,
  },
  talkToggleTitle: {
    color: LIGHT,
    fontFamily: 'Inter',
    fontSize: 15,
    fontWeight: '700',
  },
  input: {
    backgroundColor: 'rgba(255,255,255,0.08)',
    borderWidth: 1,
    borderColor: 'rgba(226,222,222,0.25)',
    borderRadius: 10,
    paddingHorizontal: 12,
    paddingVertical: 10,
    color: LIGHT,
    fontSize: 15,
    fontFamily: 'Inter',
  },
  posterPicker: {
    width: 180,
    aspectRatio: 400 / 565,
    borderWidth: 2,
    borderStyle: 'dashed',
    borderColor: GREEN,
    borderRadius: 12,
    overflow: 'hidden',
    alignItems: 'center',
    justifyContent: 'center',
  },
  posterPreview: {
    width: '100%',
    height: '100%',
  },
  pickerEmpty: {
    alignItems: 'center',
    gap: 6,
    padding: 10,
  },
  pickerText: {
    color: LIGHT,
    fontSize: 13,
    fontFamily: 'Inter',
    textAlign: 'center',
  },
  linkRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 8,
  },
  iconButton: {
    padding: 4,
  },
  addButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    alignSelf: 'flex-start',
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 16,
    borderWidth: 1,
    borderStyle: 'dashed',
    borderColor: GREEN,
  },
  addButtonText: {
    color: GREEN,
    fontSize: 12,
    fontWeight: '600',
    fontFamily: 'Inter',
  },
  selectedVenue: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderWidth: 1,
    borderColor: GREEN,
    borderRadius: 10,
    paddingLeft: 12,
    paddingRight: 6,
    paddingVertical: 6,
  },
  selectedVenueText: {
    flex: 1,
    color: LIGHT,
    fontSize: 15,
    fontFamily: 'Inter',
  },
  venueOption: {
    paddingVertical: 9,
    paddingHorizontal: 12,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(226,222,222,0.12)',
  },
  venueOptionText: {
    color: LIGHT,
    fontSize: 14,
    fontFamily: 'Inter',
  },
  organizerRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
  },
  organizerPhoto: {
    width: 77,
    height: 77,
    backgroundColor: '#fff',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  error: {
    color: '#ff7b7b',
    fontSize: 14,
    fontFamily: 'Inter',
  },
  actions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 10,
  },
  cancelButton: {
    borderWidth: 1,
    borderColor: LIGHT,
    borderRadius: 10,
    paddingVertical: 11,
    paddingHorizontal: 18,
    minWidth: 92,
    alignItems: 'center',
  },
  cancelText: {
    color: LIGHT,
    fontFamily: 'Inter',
    fontWeight: '600',
  },
  submitButton: {
    backgroundColor: GREEN,
    borderRadius: 10,
    paddingVertical: 11,
    paddingHorizontal: 18,
    minWidth: 92,
    alignItems: 'center',
  },
  submitText: {
    color: PAGE_BG,
    fontFamily: 'Inter',
    fontWeight: '700',
  },
});

export default EventPostFormModal;
